import { getStored, setStored } from "@/lib/storage";
import { DEFAULTS, PREF, type OpenTo } from "@/lib/prefs";
import { parseHash, replaceHash, type Location, type Route } from "@/router";

/** Screens worth reopening. Task links and search are transient; they belong to their list. */
const REMEMBERED = new Set<Route["name"]>(["today", "inbox", "upcoming", "review", "snoozed", "all", "area", "space", "project", "settings"]);

/** Records the current screen for "Open to: Last view". */
export function rememberRoute(loc: Location) {
  if (!REMEMBERED.has(loc.route.name)) return;
  const q = loc.params.toString();
  setStored(PREF.lastRoute, `#/${loc.path}${q ? `?${q}` : ""}`);
}

/**
 * Hash to open when Desk starts without one. An explicit hash (a link, a reload, a notification)
 * always wins, so this only fills an empty start. Returns null to keep the default (Today).
 */
export function launchHash(currentHash: string, openTo: OpenTo = getStored(PREF.openTo, DEFAULTS.openTo)): string | null {
  if (currentHash.replace(/^#\/?/, "") !== "") return null;
  if (openTo === "inbox") return "#/inbox";
  if (openTo === "last") {
    const last = getStored<string | null>(PREF.lastRoute, null);
    if (typeof last !== "string" || !last.startsWith("#/")) return null;
    const route = parseHash(last).route;
    return REMEMBERED.has(route.name) && route.name !== "today" ? last : null;
  }
  return null;
}

/** True when a restored container route points at something no longer in the sidebar. */
export function missingContainer(route: Route, has: (kind: "area" | "space" | "project", id: string) => boolean): boolean {
  return (route.name === "area" || route.name === "space" || route.name === "project") && !has(route.name, route.id);
}

let restored: string | null = null;

/** Settings → Open to, applied once before the first render. */
export function applyLaunchRoute() {
  const hash = launchHash(window.location.hash);
  if (!hash) return;
  restored = hash;
  replaceHash(hash);
}

/**
 * The restored launch hash, once. The app checks it against the loaded sidebar and falls back to
 * Today when that container was archived or deleted since.
 */
export function takeRestoredLaunch(): Route | null {
  const r = restored;
  restored = null;
  return r ? parseHash(r).route : null;
}
